import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePerimetre } from '../site/usePerimetre';
import {
  estDatee,
  useImmersions,
  useValiderImmersion,
  urlCompteRenduImmersion,
  type LigneImmersionDatee,
} from '../lib/useMatching';
import {
  EnteteEcran,
  Carte,
  Pastille,
  Chargement,
  MessageErreur,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { Icone } from '../components/Icone';
import { ModaleCompteRendu } from '../components/immersion/ModaleCompteRendu';
import { LienOrganisation } from '../components/repertoire/LienOrganisation';
import { ModificationFinImmersion } from '../components/immersion/ModificationFinImmersion';
import { VisionneusePiece } from '../components/dossier/VisionneusePiece';
import { ModaleConfirmation } from '../components/contrats/ModaleConfirmation';
import { useToast } from '../components/ui/useToast';
import { usePiegeFocus } from '../lib/usePiegeFocus';
import {
  LIBELLES_IMMERSION,
  STATUTS_IMMERSION,
  compterParStatut,
  getImmersionStatus,
  immersionATraiter,
  libelleImmersion,
  type StatutImmersionCalcule,
} from '../lib/immersion-statut';

function jour(valeur: string): string {
  return new Date(valeur).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' });
}

export function PeriodeImmersionPage(): JSX.Element {
  const { site } = usePerimetre();
  const { data, isLoading, error } = useImmersions(site);
  const validerImmersion = useValiderImmersion();
  const notifier = useToast();
  const [filtreStatut, setFiltreStatut] = useState<StatutImmersionCalcule | 'TOUS'>('TOUS');
  const [immersionSelectionnee, setImmersionSelectionnee] = useState<LigneImmersionDatee | null>(null);
  // Fenêtre de détail au clavier (TF-341) : focus dedans, `Échap` la ferme.
  const fenetreDetail = useRef<HTMLDivElement>(null);
  usePiegeFocus(fenetreDetail, immersionSelectionnee !== null, () => setImmersionSelectionnee(null));
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState<'candidat' | 'entreprise' | 'dateDebut' | 'dateFin'>('dateDebut');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [immersionEvaluer, setImmersionEvaluer] = useState<LigneImmersionDatee | null>(null);
  const [immersionAValider, setImmersionAValider] = useState<LigneImmersionDatee | null>(null);
  // Compte rendu consulté : la visionneuse est celle du dossier apprenant, pas
  // une seconde mécanique d'aperçu.
  const [compteRenduVu, setCompteRenduVu] = useState<LigneImmersionDatee | null>(null);
  // Filtre indépendant du statut, et cumulable avec lui : « à traiter » n'est
  // pas un quatrième statut, c'est une immersion terminée sans compte rendu.
  const [aTraiterSeulement, setATraiterSeulement] = useState(false);
  const ITEMS_PER_PAGE = 10;

  const immersions = (data?.immersions ?? []).filter(estDatee);

  // Le filtre lit le statut **calculé**, comme le badge et les compteurs : le
  // champ `statut` de l'API porte le cycle de décision et peut être en retard
  // sur les dates. Statut et recherche se cumulent, ils ne s'écrasent pas.
  const immersionsFiltrees = immersions.filter(i => {
    const matchStatut = filtreStatut === 'TOUS' ? true : getImmersionStatus(i) === filtreStatut;
    const recherche = searchQuery.trim().toLowerCase();
    const matchSearch =
      recherche === '' ||
      i.candidat.toLowerCase().includes(recherche) ||
      i.entreprise.toLowerCase().includes(recherche);
    const matchATraiter = !aTraiterSeulement || immersionATraiter(i);
    return matchStatut && matchSearch && matchATraiter;
  });

  const aTraiter = immersions.filter(immersionATraiter).length;

  const immersionsTriees = [...immersionsFiltrees].sort((a, b) => {
    const aVal = a[sortKey];
    const bVal = b[sortKey];
    
    let aComp: string | number = aVal;
    let bComp: string | number = bVal;
    
    if (sortKey === 'dateDebut' || sortKey === 'dateFin') {
      aComp = new Date(aVal).getTime();
      bComp = new Date(bVal).getTime();
    } else {
      aComp = String(aVal).toLowerCase();
      bComp = String(bVal).toLowerCase();
    }

    if (aComp < bComp) return sortDirection === 'asc' ? -1 : 1;
    if (aComp > bComp) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.max(1, Math.ceil(immersionsTriees.length / ITEMS_PER_PAGE));
  // Un filtre plus étroit peut laisser `currentPage` au-delà de la dernière
  // page : la liste paraîtrait vide alors que le compteur annonce des lignes.
  const pageCourante = Math.min(currentPage, totalPages);
  const immersionsPaginees = immersionsTriees.slice(
    (pageCourante - 1) * ITEMS_PER_PAGE,
    pageCourante * ITEMS_PER_PAGE
  );

  // Mêmes chiffres que les filtres, par construction : une seule fonction.
  const compteurs = compterParStatut(immersions);

  const confirmerValidation = (): void => {
    if (!immersionAValider) return;
    validerImmersion.mutate(immersionAValider.id, {
      onSuccess: ({ message }) => {
        notifier(message);
        setImmersionAValider(null);
        setImmersionSelectionnee(null);
      },
      onError: () => notifier("L’immersion n’a pas pu être validée."),
    });
  };

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Périodes d'immersion"
        sousTitre="Gestion des périodes d'immersion en entreprise"
      />
      <BarreSites />

      {/* Compteurs et filtres partagent la même fonction de statut : le chiffre
          affiché est exactement le nombre de lignes obtenu au clic. */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <button
          type="button"
          onClick={() => { setFiltreStatut('TOUS'); setCurrentPage(1); }}
          aria-pressed={filtreStatut === 'TOUS'}
          className={`overflow-hidden rounded-2xl bg-white p-4 sm:p-6 text-center shadow-card transition hover:shadow-lg ${
            filtreStatut === 'TOUS' ? 'ring-2 ring-codialis-navy' : ''
          }`}
        >
          <div className="text-3xl font-bold text-codialis-navy">{immersions.length}</div>
          <div className="text-sm text-codialis-gray">Total des immersions</div>
        </button>
        {STATUTS_IMMERSION.map(statut => (
          <button
            key={statut}
            type="button"
            onClick={() => { setFiltreStatut(statut); setCurrentPage(1); }}
            aria-pressed={filtreStatut === statut}
            className={`overflow-hidden rounded-2xl bg-white p-4 sm:p-6 text-center shadow-card transition hover:shadow-lg ${
              filtreStatut === statut ? 'ring-2 ring-codialis-navy' : ''
            }`}
          >
            <div className="flex justify-center">
              <Pastille texte={LIBELLES_IMMERSION[statut].texte} ton={LIBELLES_IMMERSION[statut].ton} />
            </div>
            <div className="mt-2 text-3xl font-bold text-codialis-navy">{compteurs[statut]}</div>
          </button>
        ))}
      </div>

      <Carte titre="Liste principale" sansBordure>
        <div className="p-4 border-b border-gray-100 flex flex-col gap-4">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => { setFiltreStatut('TOUS'); setCurrentPage(1); }}
              aria-pressed={filtreStatut === 'TOUS'}
              className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                filtreStatut === 'TOUS'
                  ? 'bg-codialis-navy text-white'
                  : 'bg-gray-50 text-codialis-gray hover:bg-gray-100'
              }`}
            >
              Toutes ({immersions.length})
            </button>
            {/* Filtre actif : la couleur du statut, la même que son badge. */}
            {STATUTS_IMMERSION.map(statut => (
              <button
                key={statut}
                type="button"
                onClick={() => { setFiltreStatut(statut); setCurrentPage(1); }}
                aria-pressed={filtreStatut === statut}
                className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                  filtreStatut === statut
                    ? `${LIBELLES_IMMERSION[statut].classeFiltreActif} ring-2 ring-codialis-navy`
                    : 'bg-gray-50 text-codialis-gray hover:bg-gray-100'
                }`}
              >
                {LIBELLES_IMMERSION[statut].texte} ({compteurs[statut]})
              </button>
            ))}
            {/* Séparé des trois statuts : il s'ajoute au filtre en cours au lieu
                de l'écraser — « En cours » et « à traiter » restent lisibles
                ensemble, même si la combinaison est vide. */}
            <button
              type="button"
              onClick={() => { setATraiterSeulement(v => !v); setCurrentPage(1); }}
              aria-pressed={aTraiterSeulement}
              className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                aTraiterSeulement
                  ? 'bg-[#fee2e2] text-[#991b1b] ring-2 ring-codialis-navy'
                  : 'bg-gray-50 text-codialis-gray hover:bg-gray-100'
              }`}
            >
              À traiter ({aTraiter})
            </button>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="min-w-0 flex-1 max-w-md">
              <input
                type="search"
                aria-label="Rechercher par candidat ou entreprise"
                placeholder="Rechercher par candidat ou entreprise..." 
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-codialis-sky"
              />
            </div>
            
            <div className="flex items-center gap-2 text-sm text-codialis-gray">
              <span className="font-semibold">Trier par:</span>
              <select
                aria-label="Trier par"
                value={sortKey} 
                onChange={(e) => setSortKey(e.target.value as typeof sortKey)}
                className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-codialis-sky bg-white"
              >
                <option value="dateDebut">Date de début</option>
                <option value="dateFin">Date de fin</option>
                <option value="candidat">Candidat</option>
                <option value="entreprise">Entreprise</option>
              </select>
              <button 
                onClick={() => setSortDirection(d => d === 'asc' ? 'desc' : 'asc')}
                className="px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 bg-white"
              >
                {sortDirection === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          </div>
        </div>
        
        {isLoading ? (
          <Chargement libelle="Chargement..." />
        ) : error ? (
          <div className="p-4 sm:p-6">
            <MessageErreur message="Erreur de chargement des données." />
          </div>
        ) : (
          <div className="relative overflow-x-auto">
            <table className="w-full min-w-[700px] text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-codialis-gray">
                <tr>
                  <th className="px-4 py-3 text-left font-bold">Candidat</th>
                  <th className="px-4 py-3 text-left font-bold">Entreprise</th>
                  <th className="px-4 py-3 text-left font-bold">Période</th>
                  <th className="px-4 py-3 text-left font-bold">Statut</th>
                  <th className="px-4 py-3 text-right font-bold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {immersionsPaginees.length > 0 ? (
                  immersionsPaginees.map(immersion => {
                    const libelle = libelleImmersion(immersion);
                    return (
                      <tr key={immersion.id} className="hover:bg-gray-50 transition">
                        {/* Le nom mène au dossier, ici comme partout ailleurs :
                            un seul geste standardisé, pas un bouton de plus. */}
                        <td className="px-4 py-3 font-semibold">
                          <Link
                            to={`/adm-dossier/${immersion.apprenantId}`}
                            className="text-codialis-sky hover:underline"
                          >
                            {immersion.candidat}
                          </Link>
                        </td>
                        <td className="px-4 py-3">
                          <LienOrganisation
                            id={immersion.entrepriseId}
                            nom={immersion.entreprise}
                            classeLien="text-codialis-sky hover:underline"
                          />
                        </td>
                        <td className="px-4 py-3 text-xs text-codialis-gray">
                          {jour(immersion.dateDebut)} → {jour(immersion.dateFin)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <Pastille texte={libelle.texte} ton={libelle.ton} />
                            {/* Terminée sans compte rendu : il reste un retour
                                d'entreprise à recueillir (CDC §5.1). */}
                            {immersionATraiter(immersion) && (
                              <Pastille texte="À traiter" ton="danger" />
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right flex flex-wrap justify-end gap-3">
                          {/* Le libellé se lit dans la donnée, pas dans l'état
                              de l'écran : après rechargement, une immersion déjà
                              évaluée propose toujours la correction. */}
                          <button
                            type="button"
                            onClick={() => setImmersionEvaluer(immersion)}
                            className="text-xs font-semibold text-codialis-sky hover:underline"
                          >
                            {immersion.compteRendu ? 'Modifier l’évaluation' : 'Évaluation'}
                          </button>

                          <button
                            type="button"
                            onClick={() => setImmersionSelectionnee(immersion)}
                            className="text-xs font-semibold text-codialis-sky hover:underline"
                          >
                            Détails
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-codialis-gray">
                      Aucune immersion trouvée.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            
            {immersionsTriees.length > 0 && (
              <div className="p-4 border-t border-gray-100 flex items-center justify-between">
                <span className="text-sm text-codialis-gray">
                  Page {pageCourante} sur {totalPages}
                </span>
                <div className="flex gap-2">
                  <button
                    disabled={pageCourante === 1}
                    onClick={() => setCurrentPage(Math.max(1, pageCourante - 1))}
                    className="px-3 py-1 rounded border border-gray-200 text-sm disabled:opacity-50 hover:bg-gray-50 bg-white"
                  >
                    Précédent
                  </button>
                  <button
                    disabled={pageCourante === totalPages}
                    onClick={() => setCurrentPage(Math.min(totalPages, pageCourante + 1))}
                    className="px-3 py-1 rounded border border-gray-200 text-sm disabled:opacity-50 hover:bg-gray-50 bg-white"
                  >
                    Suivant
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </Carte>

      {immersionEvaluer && (
        <ModaleCompteRendu
          immersion={immersionEvaluer}
          onFermer={() => setImmersionEvaluer(null)}
          onVoirCompteRendu={() => {
            setCompteRenduVu(immersionEvaluer);
            setImmersionEvaluer(null);
          }}
        />
      )}

      {compteRenduVu && (
        <VisionneusePiece
          ouverte
          libellePiece="Compte rendu d’entretien en entreprise"
          nomFichier={`compte-rendu-entretien-entreprise-${compteRenduVu.id}.pdf`}
          urlBase={urlCompteRenduImmersion(compteRenduVu.id)}
          onFermer={() => setCompteRenduVu(null)}
        />
      )}

      {immersionSelectionnee && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div
            ref={fenetreDetail}
            role="dialog"
            aria-modal="true"
            aria-labelledby="titre-detail-immersion"
            tabIndex={-1}
            className="w-full max-w-2xl rounded-2xl bg-white p-4 sm:p-6 shadow-xl max-h-[90vh] overflow-y-auto focus:outline-none"
          >
            <h2 id="titre-detail-immersion" className="mb-4 text-lg font-bold text-codialis-navy">
              Détails de l'immersion
            </h2>
            <div className="grid grid-cols-1 gap-4 text-sm mb-6 sm:grid-cols-2">
              <div><span className="font-semibold text-codialis-gray">Candidat:</span> {immersionSelectionnee.candidat}</div>
              <div><span className="font-semibold text-codialis-gray">Entreprise:</span> {immersionSelectionnee.entreprise}</div>
              <div className="space-y-2">
                <div><span className="font-semibold text-codialis-gray">Période:</span> {jour(immersionSelectionnee.dateDebut)} → {jour(immersionSelectionnee.dateFin)}</div>
                <ModificationFinImmersion
                  key={immersionSelectionnee.id}
                  immersion={immersionSelectionnee}
                  onModifiee={(ligne) => {
                    if (estDatee(ligne)) setImmersionSelectionnee(ligne);
                  }}
                />
              </div>
              <div>
                <span className="font-semibold text-codialis-gray">Statut:</span>
                <span className="ml-2">
                  <Pastille
                    texte={libelleImmersion(immersionSelectionnee).texte}
                    ton={libelleImmersion(immersionSelectionnee).ton}
                  />
                </span>
              </div>
            </div>

            <h3 className="font-bold text-codialis-navy mb-3">Évaluation / compte rendu</h3>
            {/* Un seul compte rendu par immersion : il se corrige, il ne
                s'empile pas. Les trois écrans qui l'affichent pointent le même
                document, dans sa dernière version. */}
            {immersionSelectionnee.compteRendu ? (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-100 bg-gray-50 p-4">
                <div className="flex items-center gap-3">
                  <Icone nom="document" className="h-5 w-5 shrink-0 text-codialis-sky" />
                  <div>
                    <div className="text-sm font-semibold text-codialis-navy">
                      Compte rendu d’entretien en entreprise
                    </div>
                    <div className="text-[11px] text-codialis-gray">
                      Visite du {jour(immersionSelectionnee.compteRendu.dateVisite)} · mis à jour le{' '}
                      {jour(immersionSelectionnee.compteRendu.modifieLe)}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setCompteRenduVu(immersionSelectionnee)}
                    className="inline-flex min-h-[38px] [@media(pointer:coarse)]:min-h-[40px] items-center gap-1.5 rounded-lg border border-codialis-navy/20 px-3 py-2 text-[11px] font-semibold text-codialis-navy transition hover:bg-white"
                  >
                    <Icone nom="oeil" className="h-3.5 w-3.5" />
                    Voir
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setImmersionEvaluer(immersionSelectionnee);
                      setImmersionSelectionnee(null);
                    }}
                    className="inline-flex min-h-[38px] [@media(pointer:coarse)]:min-h-[40px] items-center rounded-lg border border-codialis-navy/20 px-3 py-2 text-[11px] font-semibold text-codialis-navy transition hover:bg-white"
                  >
                    Modifier l’évaluation
                  </button>
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-gray-200 p-4 text-sm text-codialis-gray">
                Aucune évaluation réalisée.
              </div>
            )}

            {immersionSelectionnee.compteRendu?.situation === 'NORMAL' &&
              immersionSelectionnee.refusNon === null && (
                <div className="mt-4 rounded-xl border border-emerald-100 bg-emerald-50 p-4">
                  <p className="text-sm text-emerald-900">
                    {immersionSelectionnee.refusOui ??
                      'Le compte rendu est établi. Validez explicitement l’immersion pour la transmettre au pôle contrats.'}
                  </p>
                  <button
                    type="button"
                    onClick={() => setImmersionAValider(immersionSelectionnee)}
                    disabled={immersionSelectionnee.refusOui !== null}
                    className="mt-3 rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Valider l’immersion
                  </button>
                </div>
              )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setImmersionSelectionnee(null)}
                className="rounded-lg bg-gray-100 px-4 py-2 font-semibold text-codialis-gray hover:bg-gray-200"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      <ModaleConfirmation
        ouverte={immersionAValider !== null}
        titre="Valider l’immersion"
        message={
          immersionAValider
            ? `L’immersion de ${immersionAValider.candidat} chez ${immersionAValider.entreprise} sera transmise au pôle contrats pour établir le CERFA.`
            : ''
        }
        libelleConfirmer="Valider l’immersion"
        enCours={validerImmersion.isPending}
        onConfirmer={confirmerValidation}
        onAnnuler={() => setImmersionAValider(null)}
      />
    </div>
  );
}
